import React, {useEffect, useState} from "react";
import {Box, Button, FormControl, InputLabel, ListItem, MenuItem, Select, Typography} from "@mui/material";
import {useAppDispatch} from "../../../hooks/useAppRedux";
import {CaseDialog} from "../../cases/layoutComponents/CaseDialog";
import {MediaGroupNode, PublishInformation} from '../Types';
import {DatePicker} from "@mui/x-date-pickers";

import {Image} from '../../../api/caseApiTypes';
import styles from './PublishGroupDialog.module.css';
import {formatISO, parseISO} from "date-fns";
import { publishMediaGroup, unpublishMediaGroup } from "../mediaGroupsSlice";

interface PublishGroupDialogProps {
  isVisible: boolean,
  mediaGroup?: MediaGroupNode,
  images?: Image[],
  closeDialog: () => void;
}

export const PublishGroupDialog: React.FC<PublishGroupDialogProps> = ({isVisible, mediaGroup, images, closeDialog}) => {
  const dispatch = useAppDispatch();
  const saveText = mediaGroup?.published ? 'Uppdatera' : 'Publicera';

  useEffect(() => {
    SetPublishInformation({
      firstPublishedAt: mediaGroup?.published ? mediaGroup.published.firstPublishedAt : '',
      lastPublishedAt: mediaGroup?.published ? mediaGroup.published.lastPublishedAt : '',
      activeFrom: mediaGroup?.published ? mediaGroup.published.activeFrom : '',
      activeTo: mediaGroup?.published ? mediaGroup.published.activeTo : '',
      editorType: mediaGroup?.published ? mediaGroup.published.editorType : '',
      coverImage: mediaGroup?.published ? mediaGroup.published.coverImage : ''
    });
  }, [mediaGroup]);

  useEffect(() => {
    if (!images || images.length === 0) {
      setSelectedImage('');
      return;
    }

    const coverImage = mediaGroup?.published?.coverImage;

    const exists = images.some(img => img.id === coverImage);

    setSelectedImage(exists ? coverImage : '');
  }, [mediaGroup, images]);

  const [publishInformation, SetPublishInformation] = useState<PublishInformation>(
    {
      firstPublishedAt: mediaGroup?.published ? mediaGroup.published.firstPublishedAt : '',
      lastPublishedAt: mediaGroup?.published ? mediaGroup.published.lastPublishedAt : '',
      activeFrom: mediaGroup?.published ? mediaGroup.published.activeFrom : '',
      activeTo: mediaGroup?.published ? mediaGroup.published.activeTo : '',
      editorType: mediaGroup?.published ? mediaGroup.published.editorType : '',
      coverImage: mediaGroup?.published ? mediaGroup.published.coverImage : ''
    }
  );
  const [isValid, SetIsValid] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string | undefined>('');

  const onSave = () => {
    if (isValid) {
      dispatch(publishMediaGroup({node: mediaGroup!, publishInformation: publishInformation}));
      closeDialog();
    }
  }

  const onClose = () => {
    SetPublishInformation({
      firstPublishedAt: mediaGroup?.published ? mediaGroup.published.firstPublishedAt : '',
      lastPublishedAt: mediaGroup?.published ? mediaGroup.published.lastPublishedAt : '',
      activeFrom: mediaGroup?.published ? mediaGroup.published.activeFrom : '',
      activeTo: mediaGroup?.published ? mediaGroup.published.activeTo : '',
      editorType: mediaGroup?.published ? mediaGroup.published.editorType : '',
      coverImage: mediaGroup?.published ? mediaGroup.published.coverImage : ''
    });
    closeDialog();
  }

  const onChange = (updates: Partial<PublishInformation>) => {
    SetPublishInformation(prevState => {
      const newState = {
        ...prevState,
        ...updates
      };

      const validateDateRange = (): boolean => {
        if (!newState.activeFrom || !newState.activeTo) {
          return true;
        }

        const fromDate = new Date(newState.activeFrom);
        const toDate = new Date(newState.activeTo);

        // Check if activeTo is earlier than activeFrom
        return toDate >= fromDate;
      };

      // Validate all properties
      const isValid = Object.entries(newState).every(([key, value]) => {
        if (key === 'firstPublishedAt' || key === 'lastPublishedAt') {
          return true;
        }

        // Skip undefined validations for optional fields
        if (key === 'activeFrom' || key === 'activeTo') {
          return value !== '' || value === undefined;
        }

        return value !== '';
      }) && validateDateRange();

      SetIsValid(isValid);

      return newState;
    });
  };

  const parseDate = (value?: string) => {
    if (value == null) {
      return null;
    }

    return parseISO(value);
  };

  const parseString = (value?: Date | null) => {
    if (value == null) {
      return;
    }

    return formatISO(value);
  };

  const unPublishButton = (
    <Button
      key="unPublishButton"
      variant="contained"
      color="error"
      onClick={() => {
        if (window.confirm(`Vill du avpublicera aktiv grupp?`)) {
          dispatch(unpublishMediaGroup({node: mediaGroup!}));
          closeDialog();
        }
      }}
    >
      Avpublicera
    </Button>
  );

  const dialogActionButtons = [];
  if (mediaGroup?.published) {
    dialogActionButtons.push(unPublishButton);
  }

  if (!isVisible) {
    return (<></>);
  }

  return (
    <CaseDialog
      isVisible={isVisible}
      saveLabel={saveText}
      saveDisabled={!isValid}
      onSave={onSave}
      onClose={onClose}
      buttons={dialogActionButtons}
    >
      <Box className={styles.dialog}>
        <Box>
          <Box>
            <Typography variant="h5">Publicera grupp</Typography>
          </Box>
        </Box>
        <Box className={styles.container}>
          <ListItem>
            <div className={styles.pickerContainer}>
              <DatePicker
                label="Aktivt från datum"
                value={parseDate(publishInformation.activeFrom)}
                onChange={v => onChange({activeFrom: parseString(v)})}
                slotProps={{textField: {fullWidth: true}}}
              />
              <DatePicker
                label="Aktivt till datum"
                value={parseDate(publishInformation.activeTo)}
                onChange={v => onChange({activeTo: parseString(v)})}
                slotProps={{textField: {fullWidth: true}}}
              />
            </div>
          </ListItem>
          <ListItem>
            <FormControl className={styles.editorContainer}>
              <InputLabel id="demo-simple-select-label">Redaktionstyp</InputLabel>
              <Select
                required
                value={publishInformation.editorType}
                label="Välj Redaktionstyp"
                onChange={e => onChange({editorType: e.target.value})}
              >
                <MenuItem key='ll' value={'ll'}>
                  Long lead
                </MenuItem>
                <MenuItem key='sl' value={'sl'}>
                  Short lead
                </MenuItem>
              </Select>
            </FormControl>
          </ListItem>

          {images && images.length > 0 && (
            <ListItem>
              <FormControl className={styles.coverImageContainer}>
                <InputLabel id="image-select-label">Välj omslagsbild</InputLabel>
                <Select
                  value={selectedImage ?? ''}
                  label="Välj bild"
                  onChange={e => {
                    const value = e.target.value as string;
                    setSelectedImage(value);
                    onChange({ coverImage: value });
                  }}
                >
                  <MenuItem value="">
                    <em>Ingen omslagsbild</em>
                  </MenuItem>
                  {images.map(image => (
                    <MenuItem key={image.id} value={image.id}>
                      {image.productName} - {image.lookupEntry}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </ListItem>
          )}
        </Box>
      </Box>
    </CaseDialog>
  );
};
